import { Injectable } from '@angular/core';
import { HttpDataService, IHttpDataCache } from '../../shared/http/http-data.service';

import { ICodeValue, ICompany, ICompanyCountry, IContact, ICountry, IDescribedObject, IGenericUser, ILocation, ILocationExtended, INamedObject, INamedStringObject, ISubCompany, IValueObject, IWebshop } from '../../../type_definitions/datatypes';
import { IUserData } from '../../shared/services/auth/auth-data.service';
import { IRoleContact } from '../webshop/info/webshop-info-data.service';
import { IDualMultiSelectElement } from '../../shared/dual-multi-select/dual-multi-select.component';

export interface IChangePasswordResponse {
  token:string;
}

export interface IUserProfileRequest {
  userRegistrationApproved?:boolean;
  userRegistrationRejected?:boolean;
  countryId:number;
  firstname:string;
  lastname:string;
  email:string;
  login:string;
  title:string;
  language:{
    isoCode:string;
  };
  gaLayout:IDescribedObject;
  companyDepartment?:ICompanyCountry;
  directPhone:string;
  officePhone:string;
  cellPhone:string;
  faxNumber:string;
  location:ILocation;
  office:string;
  subCompany:ISubCompany;
  password?:string;
  preferredCommunication?:string;
  privacyAgreed:boolean;
  comment:string;
  genericUser:IGenericUser;
  localCredentialsAlwaysAllowed:boolean;
  mailNotificationToUser?:boolean;
  signupCompanyType?:string;
  signupCountry?:ICountry;
  userTraining:{
    lastTrainingDate?:number;
    trainingStatus?:string;
    acslComment?:string;
    responsive?:boolean;
    usingGA?:boolean;
    canDemoGA?:boolean;
  };
  weChatId?:string;
  whatsappId?:string;
  mailSubscription?:IUserNotifications;
  linkedin?:string;
  fluentEnglish?:boolean;
  updateMethod?:ICodeValue;
  updateComments?:string;
  updateFrequency?:string;
  timeSlotForGAUpdates?:string;
  checkpoint?:string;
}

export interface IPermissionRequest {
  userId:number;
  genericUserId?:number;
  allWebshops?:boolean;
  selectedWebshops?:IDualMultiSelectElement[];
}

export interface ICompanyAccessSettings extends IAccessSettings {
  company:ICompany;
}

export interface IWebshopAccessSettings extends IAccessSettings {
  webshop:IWebshop;
}

interface IAccessSettings {
  approver:IContact;
  allowAddEmailSuffix:boolean;
}

export interface IAccessSettingsResponse {
  webshopAccessSettings:IWebshopAccessSettings[];
  companyAccessSettings:ICompanyAccessSettings;
}

interface ICheckUsernameResponse {
  loginAvailable:boolean;
}

interface ICheckEmailResponse {
  emailAvailable:boolean;
}

export interface IUserAuditData {
  creationUser:IContact;
  creationDate:number;
  firstLoginDate:number;
  lastLoginDate:number;
  modificationDate:number;
  modificationUser:IContact;
  registrationDate:number;
  timeWithACSL:string;
  trainingModificationDate:number;
  trainingModificationUser:IContact;
  roleContacts: IRoleContact[];
}

export interface IUserNotifications {
  mailNotificationGroups:IUserNotificationsGroup[];
}

export interface IUserNotificationsGroup extends IDescribedObject {
  subscribed:boolean;
}

@Injectable()
export class UserProfileDataService {
  private cache:IHttpDataCache<any>;

  constructor(
    private httpDataService:HttpDataService
  ) {
    this.cache = httpDataService.createCache();
  }

  public clear():void {
    this.cache.clear();
  }

  public getUserProfile(userId:number):Promise<IUserData> {
    return this.httpDataService.doGET(
      `rest/userman/userprofile/${userId}`,
      {},
      undefined,
      'Error loading user'
    );
  }

  public updateUserProfile(userId:number, userProfileData:IUserProfileRequest):Promise<IUserData> {
    return this.httpDataService.doPUT(
      `rest/userman/userprofile/${userId}`,
      undefined,
      userProfileData,
      undefined,
      'Error saving profile'
    );
  }

  public createUserProfile(userProfileData:IUserProfileRequest):Promise<IUserData> {
    return this.httpDataService.doPOST(
      `rest/userman/userprofile/`,
      undefined,
      userProfileData,
      undefined,
      'Error saving profile'
    );
  }

  public checkUsernameAvailable(login:string):Promise<boolean> {
    return this.httpDataService.doPOST(
      `rest/userman/signup/loginavailable`,
      undefined,
      { login },
      undefined,
      undefined
    )
      .then((response:ICheckUsernameResponse) => {
        return Promise.resolve(response.loginAvailable);
      });
  }

  public checkEmailAvailable(email:string):Promise<boolean> {
    return this.httpDataService.doPOST(
      `rest/userman/signup/emailavailable`,
      undefined,
      { email },
      undefined,
      undefined
    )
      .then((response:ICheckEmailResponse) => {
        return Promise.resolve(response.emailAvailable);
      });
  }

  public getUserLocations(countryId:number, companyId:number, subCompanyId?:number):Promise<ILocationExtended[]> {
    return this.httpDataService.doGET(
      `rest/userman/userprofile/locations`,
      {
        countryId,
        companyId,
        subCompanyId
      },
      undefined,
      'Error loading locations'
    );
  }

  public addLocation(companyId:number, newLocation:ILocation):Promise<ILocationExtended> {
    return this.httpDataService.doPOST(
      `rest/userman/userprofile/locations`,
      { companyId },
      newLocation,
      'Saving location',
      'Error adding location'
    );
  }

  public editLocation(companyId:number, locationId:number, locationData:ILocation):Promise<ILocationExtended> {
    return this.httpDataService.doPATCH(
      `rest/userman/userprofile/locations/${locationId}`,
      { companyId },
      locationData,
      'Saving location',
      'Error updating location'
    );
  }

  public getCompanies(companyType?:INamedStringObject, webshopId?:number, companyId?:number, reload?:boolean):Promise<ICompany[]> {
    const url = `rest/userman/userprofile/companies`;
    const params = {
      companyType: companyType && companyType.id,
      webshopId,
      companyId
    };

    if (reload) {
      this.cache.remove(url, params);
    }

    return this.cache.doGET(
      url,
      params,
      undefined,
      'Error loading companies'
    );
  }

  public getCompanyTypes():Promise<INamedObject> {
    return this.cache.doGET(
      `rest/userman/userprofile/companytypes`,
      undefined,
      undefined,
      'Error loading company types'
    );
  }

  public getUserThemes():Promise<IDescribedObject[]> {
    return this.cache.doGET(
      `rest/userman/userprofile/themes`,
      undefined,
      undefined,
      'Error loading themes'
    );
  }

  public resetPassword(userId:number):Promise<{}> {
    return this.httpDataService.doPOST(
      `rest/userman/resetpassword`,
      undefined,
      { userId },
      undefined,
      'Error reseting password'
    );
  }

  public changePassword(oldPassword:string, newPassword:string):Promise<IChangePasswordResponse> {
    return this.httpDataService.doPOST(
      `rest/userman/changepassword`,
      undefined,
      {
        oldPassword,
        newPassword
      },
      undefined,
      'Error changing password'
    );
  }

  public setPermissions(userId:number, permissionData:IPermissionRequest):Promise<{}> {
    return this.httpDataService.doPUT(
      `rest/userman/userprofile/${userId}/permissions`,
      undefined,
      permissionData,
      undefined,
      'Error saving permission'
    );
  }

  public sendUserProfile(userId:number):Promise<void> {
    return this.httpDataService.doPOST(
      `rest/userman/userprofile/${userId}/registrationinfo`,
      undefined,
      undefined,
      'Sending user profile',
      'Error sending user profile'
    );
  }

  public getAccessSettings(companyId:number, email:string, genericUserId:number, countryId?:number):Promise<IAccessSettingsResponse> {
    return this.cache.doGET(
      `rest/userman/userprofile/accesssettings`,
      {
        companyId,
        email,
        genericUserId,
        countryId
      },
      undefined,
      'Error loading access settings'
    );
  }

  public addEmailSuffix(email:string, companyId?:number, webshopId?:number):Promise<IWebshop | ICompany> {
    const url:string = webshopId? `rest/webshops/${webshopId}/emailsuffixes` : `rest/compman/company/${companyId}/emailsuffixes`;
    return this.httpDataService.doPOST(
      url,
      undefined,
      {
        email,
        webshopId
      },
      'Adding email suffix',
      'Error adding email suffix');
  }

  public sendApprovalReminder(userId:number, webshopId:number, companyId:number, countryId:number):Promise<void> {
    return this.httpDataService.doPOST(
      `rest/userman/userprofile/${userId}/approvalreminder`,
      {
        webshopId,
        companyId,
        countryId
      },
      undefined,
      'Sending reminder to approver',
      'Error sending reminder');
  }

  public getTrainingStatuses():Promise<IValueObject> {
    return this.cache.doGET(
      'rest/userman/userprofile/trainingstatuses',
      undefined,
      undefined,
      'Error loading training statuses'
    );
  }

  public getAuditData(userId:number):Promise<IUserAuditData> {
    return this.httpDataService.doGET(
      `rest/userman/userprofile/${userId}/auditdata`,
      undefined,
      undefined,
      'Error loading user audit data'
    );
  }

  public getUserNotifications(userId:number):Promise<IUserNotifications> {
    return this.httpDataService.doGET(
      `rest/userman/userprofile/${userId}/mailsubscriptions`,
      undefined,
      undefined,
      'Error loading mail notifications'
    );
  }
}